import * as React from 'react'

import { apiJson, clearAuthToken, getAuthToken, setAuthToken } from '@/lib/api'
import type { TokenResponse, User } from '@/types'

type AuthContextValue = {
  user: User | null
  loading: boolean
  login: (email: string, password: string) => Promise<void>
  register: (email: string, password: string, fullName?: string) => Promise<void>
  logout: () => void
}

const AuthContext = React.createContext<AuthContextValue | null>(null)

export function AuthProvider({ children }: { children: React.ReactNode }) {
  const [user, setUser] = React.useState<User | null>(null)
  const [loading, setLoading] = React.useState(true)

  React.useEffect(() => {
    const token = getAuthToken()
    if (!token) {
      setLoading(false)
      return
    }
    apiJson<User>('/api/v1/auth/me')
      .then((me) => setUser(me))
      .catch(() => {
        clearAuthToken()
        setUser(null)
      })
      .finally(() => setLoading(false))
  }, [])

  const handleAuth = async (path: '/login' | '/register', body: Record<string, unknown>) => {
    const data = await apiJson<TokenResponse>(`/api/v1/auth${path}`, {
      method: 'POST',
      body: JSON.stringify(body),
    })
    setAuthToken(data.access_token)
    setUser(data.user)
  }

  const login = (email: string, password: string) => handleAuth('/login', { email, password })

  const register = (email: string, password: string, fullName?: string) =>
    handleAuth('/register', { email, password, full_name: fullName })

  const logout = () => {
    clearAuthToken()
    setUser(null)
    window.location.href = '/login'
  }

  return (
    <AuthContext.Provider value={{ user, loading, login, register, logout }}>
      {children}
    </AuthContext.Provider>
  )
}

export function useAuth(): AuthContextValue {
  const ctx = React.useContext(AuthContext)
  if (!ctx) {
    throw new Error('useAuth must be used within AuthProvider')
  }
  return ctx
}
